import {Interval} from "luxon";
import {container, inject, singleton} from "tsyringe";
import {Entity} from "../domain/entities/entity";
import {Issue} from "../domain/entities/issue.entity";
import {IssueRepository} from "../domain/repositories/issue.repository";
import {StringHelper} from "../helpers/string.helper";
import {UuidHelper} from "../helpers/uuid.helper";
import {EntityManager} from "../persistence/entity.manager";
import {IntervalLoadingGuard} from "../services/interval-loading.guard";
import {TimelineRequestManager} from "../services/timeline-request-manager.service";
import {ClassOf} from "../types/class-of.type";
import {TimelineConfiguration} from "./timeline-configuration.interface";
import {TimelineRowHeader} from "./timeline-row-header.interface";

@singleton()
export class TimelineBackendConnector {

    private _issuesByDay = new Map<number, Map<string, Issue>>();

    constructor(
        @inject(IssueRepository) private readonly _issueRepository: IssueRepository,
        @inject(IntervalLoadingGuard) private readonly _intervalLoadingGuard: IntervalLoadingGuard,
        @inject(TimelineRequestManager) private readonly _requestManager: TimelineRequestManager
    ) {
    }

    public async fetchIssueOfInterval(searchInterval: Interval): Promise<Map<number, Map<string, Issue>>> {
        // Delegate to TimelineRequestManager for race condition prevention and debouncing
        const result = await this._requestManager.requestIssuesForInterval(searchInterval);
        
        // Merge new issues into existing cache
        for (const [day, issuesForDay] of result.issuesByDay.entries()) {
            if (!this._issuesByDay.has(day)) {
                this._issuesByDay.set(day, new Map<string, Issue>());
            }
            
            const existingIssuesForDay = this._issuesByDay.get(day)!;
            for (const [uid, issue] of issuesForDay.entries()) {
                existingIssuesForDay.set(uid, issue);
            }
        }
        
        // Return filtered results for requested interval
        const searchIntervalStart = searchInterval.start!.toUnixInteger();
        const searchIntervalEnd = searchInterval.end!.toUnixInteger();

        const filteredResult = new Map(Array.from(
            this._issuesByDay.entries()
        ).filter(([day, _]) => day >= searchIntervalStart && day < searchIntervalEnd));
        
        return filteredResult;
    }

    /**
     * Clears the cache for all days within the given interval.
     * Used when issues are deleted or modified to ensure fresh data on reload.
     */
    public clearIntervalCache(interval: Interval): void {
        for (const dateAsInterval of interval.splitBy({day: 1})) {
            const unixDateTime = dateAsInterval.start!.toUnixInteger();
            this._issuesByDay.delete(unixDateTime);
        }
    }

    /**
     * @deprecated Use fetchIssueOfInterval which now delegates to TimelineRequestManager.
     * This method is kept for backward compatibility but should be migrated.
     */
    public async fetchIssueOfIntervalLegacy(searchInterval: Interval): Promise<Map<number, Map<string, Issue>>> {
        const requests: Promise<any>[] = [];
        const intervalGaps = this._intervalLoadingGuard.findGapsInInterval(searchInterval);
        for (const intervalGap of intervalGaps) {
            requests.push(
                this._issueRepository
                    .findAllByInterval(intervalGap)
                    .then(issuesOfGap => {

                        intervalGap.splitBy({day: 1}).map(dayAsInterval => {
                            const unixDateTime = dayAsInterval.start!.toUnixInteger();
                            this._issuesByDay.delete(unixDateTime)
                        });

                        for (const issue of issuesOfGap) {
                            for (let i = 0; i < issue.interval.count(`days`); i++) {
                                const unixDateTime = issue.start.plus({day: i}).toUnixInteger();
                                if (!this._issuesByDay.has(unixDateTime)) {
                                    this._issuesByDay.set(unixDateTime, new Map<string, Issue>);
                                }

                                const issuesByDay = this._issuesByDay.get(unixDateTime)!;

                                if (!issuesByDay.has(issue.uid)) {
                                    issuesByDay.set(issue.uid, issue);
                                }
                            }
                        }
                        this._intervalLoadingGuard.addInterval(intervalGap)
                    })
            )
        }

        await Promise.all(requests);

        const searchIntervalStart = searchInterval.start!.toUnixInteger();
        const searchIntervalEnd = searchInterval.end!.toUnixInteger();

        return new Map(Array.from(
            this._issuesByDay.entries()
        ).filter(([day, _]) => day >= searchIntervalStart && day < searchIntervalEnd));
    }

    public async fetchRows(configurations: TimelineConfiguration[]): Promise<TimelineRowHeader<Entity>[]> {

        let timelineRows: TimelineRowHeader<Entity>[] = [];

        const requests: Promise<any>[] = [];
        for (const configuration of configurations) {
            requests.push(
                this._fetchTimelineHeaderData(configuration.baseEntity, configuration.baseEntityAlias, configuration.rowFilterUids ?? [])
                    .then(async headers => {
                        
                        return timelineRows = [...timelineRows, ...await Promise.all(headers.map(async header => {
                            header.configuration = configuration;

                            // Apply bgColorFn if present
                            if (configuration.bgColorFn) {
                                header.color = await configuration.bgColorFn(header);
                            }

                            return header;
                        }))];
                    })
            )
        }

        await Promise.all(requests);

        return timelineRows;
    }

    private async _fetchTimelineHeaderData(prototype: ClassOf<Entity>, entityAlias: string | undefined = undefined, rowFilterUids: string[] = []): Promise<TimelineRowHeader<Entity>[]> {
        const entityName = entityAlias ?? StringHelper.toKebabCase(prototype.name);
        const requestUid = UuidHelper.generateV7();
        
        const requestBody = { rowFilterUids };

        const response = await fetch(`/api/v2/timeline/${entityName}`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                'x-horst-request-uid': requestUid
            },
            body: JSON.stringify(requestBody)
        });


        if (response.status !== 200) {
            return [];
        }

        const responseText = await response.text();

        let responseJson;
        try {
            responseJson = JSON.parse(responseText);
        } catch (e) {
            console.error('JSON parse error:', e);
            return [];
        }

        const payload: TimelineRowHeader<Entity>[] = responseJson.data;

        const timelineRowHeaders = [];
        for (const timelineRowHeader of payload) {
            timelineRowHeaders.push(timelineRowHeader);

            timelineRowHeader.prototype = prototype;
            timelineRowHeader.entity = await container
                    .resolve(EntityManager)
                    .getRepositoryForEntity(prototype)
                    ?.findOneById(timelineRowHeader.uid)
                ?? null;

        }

        return timelineRowHeaders;
    }
}